import { invariant } from '../lib/invariant'
import { escapeReferenceToken } from '@criteria/json-pointer'
import {
  OpenAPI as OpenAPIv3_0,
  Operation as OperationV3_0,
  SecurityScheme as SecuritySchemeV3_0
} from '@criteria/openapi/v3.0'
import {
  OpenAPI as OpenAPIv3_1,
  Operation as OperationV3_1,
  SecurityScheme as SecuritySchemeV3_1
} from '@criteria/openapi/v3.1'
import { RequestDescriptor, updateRequest } from 'criteria-requests-ui'

type OpenAPI = OpenAPIv3_1<never> | OpenAPIv3_0<never>
type Operation = OperationV3_1<never> | OperationV3_0<never>
type SecurityScheme = SecuritySchemeV3_1 | SecuritySchemeV3_0

export function requestWithAuthorizations(
  namespace: string,
  authorizations: Record<string, any>,
  openAPI: OpenAPI,
  operation: Operation
) {
  let request: RequestDescriptor = {}
  const security = [...(operation?.security ?? []), ...(openAPI?.security ?? [])]
  for (const securityRequirement of security) {
    for (const securitySchemeName in securityRequirement) {
      const key = `/${namespace}/${escapeReferenceToken(securitySchemeName)}`
      const securityScheme = (openAPI?.components?.securitySchemes ?? {})[securitySchemeName]
      if (securityScheme && authorizations[key]) {
        request = updateRequest(request, requestWithAuthorization(authorizations[key], securityScheme))
      }
    }
  }
  return request
}

export function requestWithAuthorization(authorization: any, securityScheme: SecurityScheme) {
  let request: RequestDescriptor = {}
  try {
    switch (securityScheme.type) {
      case 'apiKey':
        invariant(typeof authorization === 'string', 'API key is not configured.')
        switch (securityScheme.in) {
          case 'query':
            const params = new URLSearchParams()
            params.set(securityScheme.name, authorization)
            request = updateRequest(request, {
              url: {
                query: params.toString()
              }
            })
            break
          case 'header':
            request = updateRequest(request, {
              headers: {
                [securityScheme.name]: authorization
              }
            })
            break
          case 'cookie':
            request = updateRequest(request, {
              headers: {
                ['Cookie']: `${securityScheme.name}=${authorization}` // TODO: test appends instead of overwrites
              }
            })
            break
          default:
            const _: never = securityScheme.in
            break
        }
        break
      case 'http':
        invariant(typeof authorization === 'string', 'Token is not configured.')
        request = updateRequest(request, {
          headers: {
            Authorization: `${securityScheme.scheme} ${authorization}`
          }
        })
        break
      case 'oauth2':
        invariant(typeof authorization === 'string', 'OAuth2 authorization is not configured.')
        request = updateRequest(request, {
          headers: {
            Authorization: authorization
          }
        })
        break
      case 'openIdConnect':
        invariant(typeof authorization === 'string', 'OpenID Connect authorization is not configured.')
        request = updateRequest(request, {
          headers: {
            Authorization: authorization
          }
        })
        break
      default:
        const _: never = securityScheme
        break
    }
  } catch (error) {
    console.error(error)
  }
  return request
}
